import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { integrationSliceSelectors } from "../../redux/integration/integration.selector";
import { kanbanActions } from "../../redux/kanban/kanban.slice";
import { kanbanSliceSelectors } from "../../redux/kanban/kanban.selector";
import { orgChatActions } from "../../redux/chat/orgChat.slice";
import { PageIcons } from "../../utils/GlobalIcons";
import { KanbanCardModifiedEvent } from "../../model/kanban/KanbanCardModifiedEvent";
import { OrgChatMessage } from "../../model/chat/OrgChatMessage";
import { socket } from "../../Socketio";
import router from "../../routes/router/Router";
import { notification } from "../../utils/local-antd";
import { integrationActions } from "../../redux/integration/integration.slice";
import { AtiraToast } from "../../utils/AtiraToast";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import { ConversationMessage } from "../../model/integration/dto/meta/MetaConversationMessage";
import { ConversationComment } from "../../model/integration/dto/meta/MetaConversationComment";
import { whatsappChatTitle } from "../../routes/integration/utils/whatsapp-chat-title";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";

/** A hand-over reuses the new-message sound rather than introducing a second
 * tune. Staff already read one sound; a second is a thing to learn, and the
 * hand-over follows a customer message that just made this exact noise anyway.
 * The toast is what distinguishes it. Flip this off if it proves too much.
 */
const HANDOVER_SOUND = true;

export const SocketConnection: React.FC = () => {
  const dispatch = useAppDispatch();
  const messageSoundRef = useRef<HTMLAudioElement>(null);
  const { t } = useTranslation();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId);
  const whatsAppMetaIntegration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const phoneNumberId = whatsAppMetaIntegration?.config?.phoneNumberId;
  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const kanbanRef = useRef(kanban);

  // Read inside a socket handler, so it is held in a ref: depending on the list
  // itself would tear down and re-register every handler on each message
  const chats = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaChatsList,
  );
  const chatsRef = useRef(chats);

  // See refreshInbox
  const refreshingRef = useRef(false);
  const askAgainRef = useRef(false);

  useEffect(() => {
    kanbanRef.current = kanban;
  }, [kanban]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    const audio = new Audio("/new-message.mp3");
    audio.volume = 0.4;
    messageSoundRef.current = audio;
  }, []);

  useEffect(() => {
    if (userId) {
      dispatch(integrationActions.getUserIntegrations({ userId }));
    }
  }, [dispatch, userId]);

  // The sidebar's WhatsApp unread badge otherwise reads an empty list until
  // the member actually opens the WhatsApp inbox - fetch it app-wide instead
  // of waiting on that page's own mount effect.
  useEffect(() => {
    if (!userId || !whatsAppMetaIntegration?._id || !phoneNumberId) {
      return;
    }

    dispatch(
      integrationActions.getWhatsAppMetaChatsList({
        userId,
        integrationId: whatsAppMetaIntegration._id,
        phoneNumberId,
      }),
    );
  }, [dispatch, userId, whatsAppMetaIntegration?._id, phoneNumberId]);

  // The server knows a socket by the token it connected with, so a different
  // account in this tab needs a fresh connection rather than the old one
  useEffect(() => {
    if (!userId) {
      return;
    }

    return () => {
      socket.disconnect();
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    // Only ids arrive: a card this user never loaded is not theirs to see, so
    // it is ignored, and one they hold is fetched again through the API
    const onKanbanCardModified = ({
      kanbanId,
      cardId,
    }: KanbanCardModifiedEvent) => {
      const current = kanbanRef.current;
      const holdsCard =
        current?._id === kanbanId &&
        current.columns?.some((column) =>
          column.cards?.some((card) => card._id === cardId),
        );

      if (holdsCard) {
        dispatch(kanbanActions.getKanbanCardById({ userId, cardId }));
      }
    };

    const onOrgChatMessageNew = (data: OrgChatMessage) => {
      dispatch(orgChatActions.messageReceived(data));

      if (data.senderId === userId) {
        return;
      }

      // Already looking at the room: the message speaks for itself, and this
      // keeps the server's read state current instead of just the badge.
      if (window.location.pathname.startsWith("/team-chat")) {
        dispatch(orgChatActions.markAsRead());
        return;
      }

      dispatch(orgChatActions.unreadIncremented());

      messageSoundRef.current!.currentTime = 0;
      messageSoundRef.current!.play().catch(() => {});
      AtiraToast.info(t("teamChat.newMessage"), {
        message: data.text,
        icon: <FontAwesomeIcon icon={PageIcons.TeamChat} color="green" />,
        style: { cursor: "pointer" },
        onClick: () => {
          router.navigate("/team-chat");
          notification.destroy();
        },
      });
    };

    const onOrgChatRead = () => {
      dispatch(orgChatActions.unreadCleared());
    };

    const onDisconnect = (reason: string) => {
      console.log("Socket disconnected:", reason);
    };

    const onConversationIn = (data: any) => {
      dispatch(integrationActions.metaInsertConversation(data));
    };

    // Taken off the chat's assignees, so it is no longer ours to see
    const onConversationRemove = (data: { _id: string }) => {
      dispatch(integrationActions.metaRemoveConversation(data));
    };

    const holds = (conversationId: string) =>
      Boolean(chatsRef.current?.some((chat) => chat._id === conversationId));

    // The list holds the pages loaded so far, so an event can name a chat it
    // does not hold - a customer from months ago writing back. The reducers
    // have nothing to apply it to, and the chat is now at the top of the inbox,
    // so the first page is fetched again: it carries the chat, and the totals.
    const refreshInbox = async () => {
      if (!userId || !whatsAppMetaIntegration?._id || !phoneNumberId) {
        return;
      }

      // Never two at once. Answers can land out of order, and the first page
      // drops chats newer than itself that it does not contain - so an older
      // answer landing last would remove the chat a newer one just brought in.
      // An event during a fetch may postdate what that fetch read, so it only
      // asks for one more round, and a burst of events costs two requests.
      if (refreshingRef.current) {
        askAgainRef.current = true;
        return;
      }

      refreshingRef.current = true;

      try {
        do {
          askAgainRef.current = false;

          try {
            await dispatch(
              integrationActions.getWhatsAppMetaChatsList({
                userId,
                integrationId: whatsAppMetaIntegration._id,
                phoneNumberId,
              }),
            ).unwrap();
          } catch {
            // The next event or reconnect tries again
          }
        } while (askAgainRef.current);
      } finally {
        refreshingRef.current = false;
      }
    };

    const onMessageIn = (data: ConversationMessage) => {
      if (!holds(data.conversationId)) {
        refreshInbox();
      }

      dispatch(integrationActions.metaAppendINMessage(data));

      // A chat that only just arrived is not in the list this render read, so
      // the message's own sender stands in - it is the customer's number
      const chat = chatsRef.current?.find((c) => c._id === data.conversationId);
      const who =
        chat?.name?.trim() ||
        (/^\d+$/.test(data.from || "") ? `+${data.from}` : "") ||
        whatsappChatTitle(chat);

      messageSoundRef.current!.currentTime = 0;
      messageSoundRef.current!.play().catch(() => {});
      AtiraToast.info(
        data.text?.body || t(`common.whatsapp.${data.type}`, ""),
        {
          message: who
            ? t("common.whatsapp.newMessageFrom", { name: who })
            : t("common.whatsapp.newMessage"),
          icon: <FontAwesomeIcon icon={faWhatsapp} color="green" />,
          style: { cursor: "pointer" },
          // This component lives outside the RouterProvider, so navigation goes
          // through the router instance rather than a hook
          onClick: () => {
            router.navigate(
              `/integration/whatsapp-meta?chat=${data.conversationId}`,
            );
            notification.destroy();
          },
        },
      );
    };

    const onMessageOut = (data: any) => {
      if (!holds(data.conversationId)) {
        refreshInbox();
      }

      dispatch(integrationActions.metaAppendOUTMessage(data));
    };

    // Comments have their own events: they belong to the chat, not to any
    // message in it
    const onCommentChanged = (data: ConversationComment) => {
      dispatch(integrationActions.metaUpsertComment(data));
    };

    const onCommentDeleted = (data: {
      _id: string;
      conversationId: string;
    }) => {
      dispatch(integrationActions.metaRemoveComment(data));
    };

    const onMessageStatus = (data: any) => {
      dispatch(integrationActions.metaUpdateMessage(data));
    };

    const onConversationUpdate = (data: any) => {
      if (!holds(data._id)) {
        refreshInbox();
      }

      dispatch(integrationActions.metaUpdateConversation(data));
    };

    const onConversationHandover = (data: {
      conversationId: string;
      name?: string;
      waId: string;
      agentName?: string;
      reason?: string;
    }) => {
      if (!holds(data.conversationId)) {
        refreshInbox();
      }

      if (HANDOVER_SOUND) {
        messageSoundRef.current!.currentTime = 0;
        messageSoundRef.current!.play().catch(() => {});
      }

      // Title and description are passed the way onMessageIn does it: the first
      // argument is the description, `config.message` overrides the title
      AtiraToast.warning(
        t("integration.whatsapp.meta.handover.toast.body", {
          contact: data.name || data.waId,
          reason:
            data.reason ||
            t("integration.whatsapp.meta.handover.noReasonGiven"),
        }),
        {
          message: t("integration.whatsapp.meta.handover.toast.title", {
            name:
              data.agentName ||
              t("integration.whatsapp.meta.aiAgent.defaultName"),
          }),
          icon: <FontAwesomeIcon icon={faRobot} color="#fd7e14" />,
          style: { cursor: "pointer" },
          onClick: () => {
            router.navigate(
              `/integration/whatsapp-meta?chat=${data.conversationId}`,
            );
            notification.destroy();
          },
        },
      );
    };

    const onMessageDelete = (data: ConversationMessage) => {
      // An API from before deletions were kept sends the id alone
      dispatch(
        data.deletedAt
          ? integrationActions.metaMarkMessageDeleted(data)
          : integrationActions.metaRemoveMessage(data),
      );
    };

    const onConversationSetUnread = (data: any) => {
      if (!holds(data._id)) {
        refreshInbox();
      }

      dispatch(integrationActions.metaSetUnreadConversationCount(data));
    };

    socket.on("kanban.card.modified", onKanbanCardModified);
    socket.on("org-chat.message.new", onOrgChatMessageNew);
    socket.on("org-chat.read", onOrgChatRead);

    // The inbox events wait on the integration, which a member has to load
    if (whatsAppMetaIntegration?._id) {
      socket.on("disconnect", onDisconnect);

      socket.on("conversation.in", onConversationIn);
      socket.on("conversation.remove", onConversationRemove);
      socket.on("message.in", onMessageIn);
      socket.on("message.out", onMessageOut);
      socket.on("message.status", onMessageStatus);
      socket.on("message.delete", onMessageDelete);
      socket.on("comment.created", onCommentChanged);
      socket.on("comment.updated", onCommentChanged);
      socket.on("comment.deleted", onCommentDeleted);
      socket.on("conversation.update", onConversationUpdate);
      socket.on("conversation.handover", onConversationHandover);
      socket.on("conversation.set-unread", onConversationSetUnread);

      // Whatever was said while the connection was down never arrives. The
      // client reconnects on its own and fires this - not on the first connect.
      socket.io.on("reconnect", refreshInbox);
    }

    // No room to join: the server puts the socket in its user's room once it
    // has checked the token, and only sends that user what they may see.
    // `active`, not `connected`: this effect re-runs as soon as the integration
    // loads, often mid-handshake, and a second connect then opens a second
    // session on the server - every event, and so every toast, arrives twice
    if (!socket.active) {
      socket.connect();
    }

    return () => {
      socket.off("disconnect", onDisconnect);

      socket.off("conversation.in", onConversationIn);
      socket.off("conversation.remove", onConversationRemove);
      socket.off("message.in", onMessageIn);
      socket.off("message.out", onMessageOut);
      socket.off("message.status", onMessageStatus);
      socket.off("message.delete", onMessageDelete);
      socket.off("comment.created", onCommentChanged);
      socket.off("comment.updated", onCommentChanged);
      socket.off("comment.deleted", onCommentDeleted);
      socket.off("conversation.update", onConversationUpdate);
      socket.off("conversation.handover", onConversationHandover);
      socket.off("conversation.set-unread", onConversationSetUnread);
      socket.io.off("reconnect", refreshInbox);

      socket.off("kanban.card.modified", onKanbanCardModified);
      socket.off("org-chat.message.new", onOrgChatMessageNew);
      socket.off("org-chat.read", onOrgChatRead);
    };
  }, [userId, whatsAppMetaIntegration?._id, phoneNumberId, t, dispatch]);

  return null;
};
